/**
 * useGreeting Hook
 *
 * Smart greeting logic that adapts based on:
 * - New user signup (account created within last hour)
 * - First dashboard visit
 * - Time of day
 * - Returning user sessions
 */

import { useMemo, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'

interface GreetingResult {
  greeting: string
  subtitle: string
  isNewUser: boolean
  isFirstVisit: boolean
}

const FIRST_VISIT_KEY = 'superschema_first_dashboard_visit'
const NEW_USER_THRESHOLD_MS = 60 * 60 * 1000 // 1 hour

function getTimeOfDayGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function hasVisitedBefore(userId: string): boolean {
  try {
    const visited = localStorage.getItem(`${FIRST_VISIT_KEY}_${userId}`)
    return visited === 'true'
  } catch {
    return false
  }
}

function markAsVisited(userId: string): void {
  try {
    localStorage.setItem(`${FIRST_VISIT_KEY}_${userId}`, 'true')
  } catch {
    // Ignore localStorage errors
  }
}

export function useGreeting(): GreetingResult {
  const { user } = useUser()

  const firstName = user?.firstName || 'there'
  const userId = user?.id || ''
  const createdAt = user?.createdAt ? new Date(user.createdAt).getTime() : 0

  // Check if user is new (account created within the last hour)
  const isNewUser = useMemo(() => {
    if (!createdAt) return false
    const now = Date.now()
    return now - createdAt < NEW_USER_THRESHOLD_MS
  }, [createdAt])

  // Check if this is the user's first visit to the dashboard
  const isFirstVisit = useMemo(() => {
    if (!userId) return false
    return !hasVisitedBefore(userId)
  }, [userId])

  // Mark as visited after render (only on first visit)
  useEffect(() => {
    if (userId && isFirstVisit) {
      // Small delay to ensure the "first visit" experience is shown
      const timer = setTimeout(() => {
        markAsVisited(userId)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [userId, isFirstVisit])

  // Generate the appropriate greeting
  const result = useMemo((): GreetingResult => {
    const timeGreeting = getTimeOfDayGreeting()

    if (isNewUser) {
      // Brand new user - just signed up
      return {
        greeting: `Welcome, ${firstName}!`,
        subtitle: "You're all set up. Let's boost your AI visibility.",
        isNewUser: true,
        isFirstVisit: true,
      }
    }

    if (isFirstVisit) {
      // Existing account but first dashboard visit (e.g., just joined a team)
      return {
        greeting: `Welcome, ${firstName}!`,
        subtitle: "Great to have you here. Let's explore your dashboard.",
        isNewUser: false,
        isFirstVisit: true,
      }
    }

    // Returning user
    return {
      greeting: `${timeGreeting}, ${firstName}!`,
      subtitle: 'Your AI visibility overview at a glance.',
      isNewUser: false,
      isFirstVisit: false,
    }
  }, [firstName, isNewUser, isFirstVisit])

  return result
}
